import { useState } from 'react'
import { CheckCircle2, XCircle } from 'lucide-react'
import {
  Badge,
  Button,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  StatusBadge,
  useToast,
} from '@/components/ui'
import { useAffectedContracts, useBulkSend, useTemplateDiff, useTemplateVersions } from '../api/templatesApi.ts'
import type { AffectedContract, BulkSendResult, ContractTemplate } from '../types.ts'

type Step = 'affected' | 'diff' | 'compose' | 'result'

interface Props {
  template: ContractTemplate | null
  open: boolean
  onOpenChange: (o: boolean) => void
}

export function BulkSendWizard({ template, open, onOpenChange }: Props) {
  const { toast } = useToast()
  const [step, setStep] = useState<Step>('affected')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [emailMap, setEmailMap] = useState<Record<string, string>>({})
  const [subject, setSubject] = useState('')
  const [sendResult, setSendResult] = useState<BulkSendResult | null>(null)

  const templateId = template?.id ?? null

  const { data: versions = [] } = useTemplateVersions(templateId)
  const prevVersion = versions.length > 1 ? versions[versions.length - 2] : null

  const { data: affected = [], isLoading: affLoading, error: affError } = useAffectedContracts(templateId)
  const { data: diff, isLoading: diffLoading } = useTemplateDiff(
    templateId,
    prevVersion?.id ?? null,
  )
  const bulkSend = useBulkSend(templateId ?? '')

  function handleClose() {
    setStep('affected')
    setSelected(new Set())
    setEmailMap({})
    setSubject('')
    setSendResult(null)
    onOpenChange(false)
  }

  function toggleContract(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    if (selected.size === affected.length) setSelected(new Set())
    else setSelected(new Set(affected.map((a) => a.contractId)))
  }

  function handleSend() {
    if (!templateId) return
    const recipients = [...selected].map((contractId) => ({
      contractId,
      email: emailMap[contractId] ?? '',
    })).filter((r) => r.email.includes('@'))

    if (recipients.length === 0) {
      toast({ title: 'Keine gültigen E-Mail-Adressen', tone: 'danger' })
      return
    }

    bulkSend.mutate(
      { recipients, subject: subject || undefined },
      {
        onSuccess: (data) => {
          setSendResult(data)
          setStep('result')
        },
        onError: (err) => {
          toast({ title: 'Versand fehlgeschlagen', description: err.message, tone: 'danger' })
        },
      },
    )
  }

  const title = {
    affected: 'Betroffene Verträge',
    diff: 'Änderungsübersicht',
    compose: 'E-Mail verfassen & versenden',
    result: 'Versand-Ergebnis',
  }[step]

  const footer = (
    <>
      {step === 'affected' && (
        <>
          <Button variant="secondary" onClick={handleClose}>Abbrechen</Button>
          {affected.length > 0 && (
            <Button variant="primary" disabled={selected.size === 0} onClick={() => setStep('diff')}>
              Weiter ({selected.size})
            </Button>
          )}
        </>
      )}
      {step === 'diff' && (
        <>
          <Button variant="secondary" onClick={() => setStep('affected')}>Zurück</Button>
          <Button variant="primary" onClick={() => setStep('compose')}>Weiter</Button>
        </>
      )}
      {step === 'compose' && (
        <>
          <Button variant="secondary" onClick={() => setStep('diff')}>Zurück</Button>
          <Button variant="primary" loading={bulkSend.isPending} onClick={handleSend}>
            Jetzt versenden
          </Button>
        </>
      )}
      {step === 'result' && (
        <Button variant="primary" onClick={handleClose}>Schließen</Button>
      )}
    </>
  )

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      size="lg"
      title={`Update-Versand: ${template?.name ?? ''}`}
      description={title}
      footer={footer}
    >
      <div className="space-y-4 min-h-[300px]">

        {step === 'affected' && (
          <>
            {affLoading && <LoadingState />}
            {affError && <ErrorState title="Fehler beim Laden" description="Betroffene Verträge konnten nicht geladen werden." />}
            {!affLoading && !affError && affected.length === 0 && (
              <div className="text-center py-8 text-sm text-gray-500">
                Alle Verträge nutzen bereits die aktuelle Version. Kein Versand nötig.
              </div>
            )}
            {!affLoading && !affError && affected.length > 0 && (
              <>
                <p className="text-sm text-gray-600">
                  {affected.length} Vertrag{affected.length !== 1 ? 'e haben' : ' hat'} noch Dokumente aus älteren Versionen.
                  Wähle die Verträge, für die ein Update versendet werden soll.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 pb-1 border-b border-gray-200">
                    <input
                      type="checkbox"
                      id="select-all"
                      checked={selected.size === affected.length && affected.length > 0}
                      onChange={toggleAll}
                      className="rounded"
                    />
                    <label htmlFor="select-all" className="text-caption font-medium text-gray-600 cursor-pointer">
                      Alle auswählen
                    </label>
                  </div>
                  {affected.map((c: AffectedContract) => (
                    <label key={c.contractId} className="flex items-center gap-3 cursor-pointer rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selected.has(c.contractId)}
                        onChange={() => toggleContract(c.contractId)}
                        className="rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{c.partyName ?? '—'}</span>
                          <span className="text-caption text-gray-500">#{c.contractNumber}</span>
                          <StatusBadge status={c.status} />
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {step === 'diff' && (
          <>
            {diffLoading && <LoadingState />}
            {!diffLoading && !diff && (
              <p className="text-sm text-gray-500 text-center py-8">Keine Vorgängerversion für Diff verfügbar.</p>
            )}
            {!diffLoading && diff && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Vergleich v{diff.b.version} → v{diff.a.version}
                </p>
                {diff.diff.added.length > 0 && (
                  <div>
                    <p className="text-caption font-medium text-success-700 mb-2">+ Neue Platzhalter</p>
                    <div className="flex flex-wrap gap-1">
                      {diff.diff.added.map((p) => <Badge key={p} variant="success" size="sm">{p}</Badge>)}
                    </div>
                  </div>
                )}
                {diff.diff.removed.length > 0 && (
                  <div>
                    <p className="text-caption font-medium text-danger-700 mb-2">− Entfernte Platzhalter</p>
                    <div className="flex flex-wrap gap-1">
                      {diff.diff.removed.map((p) => <Badge key={p} variant="danger" size="sm">{p}</Badge>)}
                    </div>
                  </div>
                )}
                {diff.diff.unchanged.length > 0 && (
                  <div>
                    <p className="text-caption font-medium text-gray-500 mb-2">Unverändert</p>
                    <div className="flex flex-wrap gap-1">
                      {diff.diff.unchanged.map((p) => <Badge key={p} variant="neutral" size="sm">{p}</Badge>)}
                    </div>
                  </div>
                )}
                {diff.diff.added.length === 0 && diff.diff.removed.length === 0 && (
                  <p className="text-sm text-gray-500">Keine Platzhalter-Unterschiede zwischen den Versionen.</p>
                )}
              </div>
            )}
          </>
        )}

        {step === 'compose' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              E-Mail-Adressen der Empfänger eingeben und Betreff anpassen.
            </p>
            <Input
              label="Betreff"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`Vertragsvorlage aktualisiert: ${template?.name ?? ''}`}
            />
            <div className="space-y-2">
              {[...selected].map((contractId) => {
                const c = affected.find((a) => a.contractId === contractId)
                return (
                  <div key={contractId} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-caption text-gray-600 truncate">{c?.partyName ?? contractId} (#{c?.contractNumber})</p>
                    </div>
                    <Input
                      className="w-64"
                      placeholder="empfaenger@beispiel.de"
                      value={emailMap[contractId] ?? ''}
                      onChange={(e) => setEmailMap((prev) => ({ ...prev, [contractId]: e.target.value }))}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {step === 'result' && sendResult && (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              {sendResult.results.filter((r) => r.ok).length} von {sendResult.results.length} E-Mails erfolgreich versendet.
            </p>
            <div className="space-y-2">
              {sendResult.results.map((r) => (
                <div key={r.contractId + r.email} className="flex items-center gap-2 text-sm">
                  {r.ok
                    ? <CheckCircle2 className="w-4 h-4 text-success-600 shrink-0" />
                    : <XCircle className="w-4 h-4 text-danger-600 shrink-0" />
                  }
                  <span className="truncate">{r.email}</span>
                  {r.error && <span className="text-caption text-danger-600 truncate">{r.error}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
